import Service, { inject as service } from '@ember/service';
import ENV from '../config/environment';

/**
 * The prefix we'll use to ensure we don't clash on other namespaces.
 * @type {string}
 */
const STORAGE_PREFIX = '__SH';

/**
 * Storage keys for our shared store.
 *
 * We'll keep the list of these keys here since we don't want to allow any name collisions.
 *
 * @type {Object}
 */
export const STORAGE_KEYS = {
  /**
   * This is the key used to store whether or not our heartbeat has been paused.
   */
  HEARTBEAT_PAUSED_STORAGE_KEY : 'hbpsk',

  /**
   * Used to mark that the user has joined a company already in the DIY flow.
   *
   * NOTE:  Should be keyed with a userId as well
   */
  HAS_JOINED_COMPANY_ACCOUNT_ALREADY : 'hjcaa',

  /**
   * Used to enable features for internal testing that are not yet enabled for general public
   */
  INTERNAL_TESTING: 'internal_testing',

  /**
   * This is used when a user navigates to a profile unauthed and needs to login.  We will save the slug of the profile
   * they want to look at.
   *
   * @type {String}
   */
  UNAUTHED_PROFILE_SLUG_TO_VIEW: 'unauth_profile_id_to_view',

  /**
   * This stores the entire authentication cookie and is used when an admin masquerades as a user
   * so they can end masquerading and be relogged back into their own profile.
   *
   * @type {String}
   */
  MASQUERADE_ORIG_AUTH: 'maquerade_orig_auth',

  /**
   * This stores the last reloaded web version. It is used to prevent infinite page reload.
   *
   * @type {String}
   */
  LAST_LOADED_WEB_VERSION: 'last_loaded_web_version',
};

/**
 * A storage cache for key value pairs that will prefer local storage when available but will fallback
 * too cookie storage if it isn't
 *
 * ******** KEEP IN MIND *********
 * Cookies are sent with every request, so this should NOT be used if you're planning
 * on keeping a lot of data around.
 */
export default Service.extend({
  /**
   * Will be initialized to true or false depending on if this browser supports local storage.
   *
   * @type {Boolean}
   * @private
   */
  _isSupportsLocalStorage: true,

  /**
   * Ember service for dealing with cookies.
   *
   * @type {Service}
   */
  cookies: service(),

  /**
   * @inheritDoc
   */
  init() {
    this._super(...arguments);

    //let's see if we support local storage or not.
    let testKey = STORAGE_PREFIX + 'sh_test_key';
    try {
      window.localStorage.setItem(testKey, '1');
      window.localStorage.removeItem(testKey);
    } catch (exception) {
      this.set('_isSupportsLocalStorage', false);
      return false;
    }
  },

  /**
   * Get the value for the given key
   *
   * @param {String} key
   * @returns {String|null}
   */
  getItem(key) {
    if (this.get('_isSupportsLocalStorage')) {
      return window.localStorage.getItem(this._getStorageKey(key));
    }

    return this.get('cookies').read(this._getStorageKey(key), {
      domain: ENV.cookieDomain,
      secure: true
    });
  },

  /**
   * Remove a value for the given key
   *
   * @param {String} key
   * @return Promise
   */
  removeItem(key) {
    if (this._isSupportsLocalStorage) {
      window.localStorage.removeItem(this._getStorageKey(key));
      return Promise.resolve();
    }

    return this.cookies.clear(this._getStorageKey(key), {
      domain: ENV.cookieDomain,
      secure: true,
    });
  },

  /**
   * Set a key value pair in storage
   *
   * @param {String} key
   * @param {String} val
   */
  setItem(key, val) {
    if (this.get('_isSupportsLocalStorage')) {
      window.localStorage.setItem(this._getStorageKey(key), val);
      return;
    }

    this.get('cookies').write(this._getStorageKey(key), val, {
      domain: ENV.cookieDomain,
      secure: true
    });
  },

  /**
   * Get the storage key for the 'key' that was passed in by the caller.  This will add prefixes for the
   * global Storyhunter object as well as the environment.
   *
   * @param {String} key
   * @return {String}
   * @private
   */
  _getStorageKey(key) {
    return `${STORAGE_PREFIX}::${ENV.environment}::${key}`;
  }
});
